// src/components/BadgeVerificationPage.js
import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Shield, Check, Award, Download, ExternalLink, Terminal, Lock, Layers, User, Calendar, Tag, Hash, AlertTriangle } from 'lucide-react';
import html2canvas from 'html2canvas';
import api from '../../../utils/apiClient';

// Animations
const scanline = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
`;

const glowPulse = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(10, 255, 112, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(10, 255, 112, 0.8);
  }
  100% {
    box-shadow: 0 0 5px rgba(10, 255, 112, 0.5);
  }
`;

const flicker = keyframes`
  0%, 19.999%, 22%, 62.999%, 64%, 64.999%, 70%, 100% {
    opacity: 0.99;
  }
  20%, 21.999%, 63%, 63.999%, 65%, 69.999% {
    opacity: 0.4;
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0);
  }
`;

const terminate = keyframes`
  0% {
    clip-path: inset(0% 35% 70% 35%);
  }
  20% {
    clip-path: inset(60% 35% 10% 35%);
  }
  40% {
    clip-path: inset(30% 35% 40% 35%);
  }
  60% {
    clip-path: inset(50% 35% 20% 35%);
  }
  80% {
    clip-path: inset(10% 35% 60% 35%);
  }
  100% {
    clip-path: inset(40% 35% 30% 35%);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.backgroundDark || '#121212'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const PageContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
  position: relative;
`;

const HeaderIcon = styled.div`
  margin: 0 auto 20px;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary || '#0AFF70'};
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.backgroundDark || '#121212'};
    z-index: -1;
  }
  
  &:after {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 50%;
    border: 2px solid rgba(10, 255, 112, 0.2);
    animation: ${glowPulse} 3s infinite ease-in-out;
  }
  
  svg {
    width: 40px;
    height: 40px;
    filter: drop-shadow(0 0 10px rgba(10, 255, 112, 0.6));
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.textPrimary || '#FFFFFF'};
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, #0AFF70, #00A8FF);
    border-radius: 999px;
  }
`;

const PageSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary || '#AAAAAA'};
  font-size: 18px;
  max-width: 600px;
  margin: 30px auto 0;
`;

const VerifiedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(to right, #121212, rgba(10, 255, 112, 0.08));
  color: ${({ theme }) => theme.colors.primary || '#0AFF70'};
  padding: 8px 20px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 14px;
  margin-top: 20px;
  border: 1px solid rgba(10, 255, 112, 0.2);
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 15px rgba(10, 255, 112, 0.2);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 1px;
    background: ${({ theme }) => theme.colors.primary || '#0AFF70'};
    animation: ${scanline} 3s linear infinite;
  }
  
  svg {
    animation: ${glowPulse} 2s infinite;
  }
`;

const BadgeCard = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLight || '#1E1E1E'};
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 15px rgba(10, 255, 112, 0.2);
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(10, 255, 112, 0.13);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #0AFF70, #00A8FF);
    z-index: 1;
  }
`;

const BadgeCardInner = styled.div`
  padding: 40px;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const BadgeBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300ff8f' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`;

const BadgeScanlines = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  background: linear-gradient(to bottom, transparent, rgba(10, 255, 112, 0.02) 50%, transparent);
  animation: ${scanline} 8s linear infinite;
  opacity: 0.5;
`;

const BadgeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const BadgeIconContainer = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const BadgeIconRing = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  border: 2px dashed rgba(10, 255, 112, 0.33);
  animation: ${float} 4s ease-in-out infinite;
  
  &:before, &:after {
    content: '';
    position: absolute;
    border-radius: 50%;
  }
  
  &:before {
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border: 1px solid rgba(10, 255, 112, 0.2);
    animation: ${float} 8s ease-in-out infinite reverse;
  }
  
  &:after {
    top: 10px;
    left: 10px;
    right: 10px;
    bottom: 10px;
    border: 1px solid rgba(10, 255, 112, 0.2);
    animation: ${float} 6s ease-in-out infinite;
  }
`;

const BadgeIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #121212, #1E1E1E);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3), 0 0 15px rgba(10, 255, 112, 0.4);
  z-index: 1;
  color: ${({ theme }) => theme.colors.primary || '#0AFF70'};
  border: 1px solid rgba(10, 255, 112, 0.2);
  
  &:before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(10, 255, 112, 0.13), transparent);
    z-index: -1;
    animation: ${glowPulse} 3s infinite;
  }
`;

const BadgeInfo = styled.div`
  flex: 1;
`;

const BadgeTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.textPrimary || '#FFFFFF'};
  text-shadow: 0 0 10px rgba(10, 255, 112, 0.13);
`;

const BadgeDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary || '#AAAAAA'};
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const BadgeTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const BadgeTag = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${({ theme, primary }) => primary ?
        `linear-gradient(to right, rgba(10, 255, 112, 0.13), rgba(10, 255, 112, 0.07))` :
        (theme.colors.backgroundDark || '#121212')};
  color: ${({ theme, primary }) => primary ? (theme.colors.primary || '#0AFF70') : (theme.colors.textSecondary || '#AAAAAA')};
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid ${({ theme, primary }) => primary ?
        `rgba(10, 255, 112, 0.2)` :
        (theme.colors.divider || '#333333')};
`;

const BadgeMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 30px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.divider || '#333333'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider || '#333333'};
  padding: 20px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MetaIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.backgroundDark || '#121212'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary || '#AAAAAA'};
  border: 1px solid ${({ theme }) => theme.colors.divider || '#333333'};
`;

const MetaContent = styled.div``;

const MetaLabel = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary || '#AAAAAA'};
  margin-bottom: 4px;
`;

const MetaValue = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary || '#FFFFFF'};
  font-family: 'Courier New', monospace;
`;

const IssuerSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const IssuerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const IssuerLogo = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.backgroundDark || '#121212'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.divider || '#333333'};
  
  svg {
    width: 30px;
    height: 30px;
    color: ${({ theme }) => theme.colors.primary || '#0AFF70'};
  }
`;

const IssuerDetails = styled.div``;

const IssuerName = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary || '#FFFFFF'};
`;

const IssuerVerified = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary || '#AAAAAA'};
  
  svg {
    color: ${({ theme }) => theme.colors.primary || '#0AFF70'};
    width: 14px;
    height: 14px;
  }
`;

const QRCode = styled.div`
  background: ${({ theme }) => theme.colors.backgroundDark || '#121212'};
  border-radius: 6px;
  padding: 8px;
  width: 100px;
  height: 100px;
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.divider || '#333333'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:before {
    content: '';
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border: 1px dashed ${({ theme }) => theme.colors.divider || '#333333'};
    z-index: 0;
  }
  
  img {
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 1;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 1px;
    background: currentColor;
    opacity: 0.3;
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    
    &:before {
      animation: ${scanline} 2s linear infinite;
    }
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PrimaryButton = styled(Button)`
  background: ${({ theme }) => theme.colors.primary || '#0AFF70'};
  color: ${({ theme }) => theme.colors.backgroundDark || '#121212'};
  box-shadow: 0 5px 15px rgba(10, 255, 112, 0.4);
  border: none;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary || '#0AFF70'};
    box-shadow: 0 8px 25px rgba(10, 255, 112, 0.5);
  }
`;

const SecondaryButton = styled(Button)`
  background: ${({ theme }) => theme.colors.backgroundDark || '#121212'};
  color: ${({ theme }) => theme.colors.textPrimary || '#FFFFFF'};
  border: 1px solid ${({ theme }) => theme.colors.divider || '#333333'};
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundDark || '#121212'};
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
`;

const LoadingIcon = styled.div`
  width: 60px;
  height: 60px;
  position: relative;
  margin-bottom: 20px;
  
  &:before, &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    border: 3px solid transparent;
  }
  
  &:before {
    border-top-color: ${({ theme }) => theme.colors.primary || '#0AFF70'};
    animation: spin 1s linear infinite;
  }
  
  &:after {
    border-left-color: ${({ theme }) => theme.colors.primary || '#0AFF70'};
    animation: spin 1.5s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary || '#AAAAAA'};
  font-size: 18px;
  position: relative;
  
  &:after {
    content: '.';
    animation: dots 1.5s infinite;
  }
  
  @keyframes dots {
    0%, 20% {
      content: '.';
    }
    40% {
      content: '..';
    }
    60%, 100% {
      content: '...';
    }
  }
`;

const ErrorContainer = styled.div`
  max-width: 500px;
  margin: 100px auto;
  padding: 30px;
  background: ${({ theme }) => theme.colors.backgroundLight || '#1E1E1E'};
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  border: 1px solid ${({ theme }) => theme.colors.divider || '#333333'};
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease forwards;
  
  &:before {
    content: 'ERROR';
    position: absolute;
    top: 40%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 120px;
    font-weight: 800;
    opacity: 0.03;
    z-index: 0;
    letter-spacing: 10px;
    animation: ${terminate} 3s infinite linear;
  }
`;

const ErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  color: ${({ theme }) => theme.colors.textError || 'crimson'};
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const ErrorTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary || '#FFFFFF'};
  margin-bottom: 12px;
  position: relative;
  z-index: 1;
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary || '#AAAAAA'};
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
`;

const ToastNotification = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.backgroundLight || '#1E1E1E'};
  color: ${({ theme }) => theme.colors.textPrimary || '#FFFFFF'};
  padding: 12px 20px;
  border-radius: 6px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3), 0 0 10px rgba(10, 255, 112, 0.2);
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 9999;
  animation: ${fadeIn} 0.3s ease forwards, ${flicker} 0.3s linear;
  border: 1px solid rgba(10, 255, 112, 0.2);
  border-left: 3px solid ${({ theme }) => theme.colors.primary || '#0AFF70'};
`;

const ToastIcon = styled.div`
  color: ${({ theme }) => theme.colors.primary || '#0AFF70'};
  animation: ${glowPulse} 2s infinite;
`;

const ToastMessage = styled.p`
  font-size: 14px;
`;

// Badge Verification Page Component
const BadgeVerificationPage = () => {
    const { verificationId } = useParams();

    const badgeRef = useRef(null);
    const [badgeData, setBadgeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        // Fetch badge data when component mounts
        const fetchBadgeData = async () => {
            try {
                setLoading(true);
                const response = await api.verification.getById(verificationId);

                if (response.success) {
                    setBadgeData(response);
                    setLoading(false);
                } else {
                    throw new Error('Failed to verify badge');
                }
            } catch (err) {
                console.error('Error fetching badge:', err);
              setError('Badge not found or inaccessible. Please verify the ID or try again later.');
                setLoading(false);
            }
        };

        if (verificationId) {
            fetchBadgeData();
        }
    }, [verificationId]);

    // Handle download badge as image
    const downloadBadgeImage = async () => {
        if (!badgeRef.current || downloading) return;

        setDownloading(true);

        try {
            const canvas = await html2canvas(badgeRef.current, {
                scale: 2,
                logging: false,
                backgroundColor: null,
                allowTaint: true,
                useCORS: true,
            });

            const image = canvas.toDataURL('image/png');

            const link = document.createElement('a');
            link.href = image;
            // Use the achievement name from the backend data for the filename
            const achievementName = badgeData.achievement_name ||
                (badgeData.achievement && badgeData.achievement.includes(' - ') ?
                    badgeData.achievement.split(' - ')[0] :
                    badgeData.achievement || 'badge');

            link.download = `${achievementName.replace(/\s+/g, '-').toLowerCase()}-badge.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            showToastNotification('Badge image downloaded successfully');
        } catch (error) {
            console.error('Error generating badge image:', error);
            showToastNotification('Failed to download badge image');
        } finally {
            setDownloading(false);
        }
    };

    // Show toast notification
    const showToastNotification = (message) => {
        setToastMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    // Visit platform
    const visitPlatform = () => {
        navigate('/');
    };

    // Handle back to achievements
    const goToAchievements = () => {
        navigate('/achievements');
    };

    // If still loading
    if (loading) {
        return (
            <PageWrapper>
                <LoadingContainer>
                    <LoadingIcon />
                    <LoadingText>Verifying badge</LoadingText>
                </LoadingContainer>
            </PageWrapper>
        );
    }

    // If error occurred
    if (error) {
        return (
            <PageWrapper>
                <ErrorContainer>
                    <ErrorIcon>
                        <AlertTriangle />
                    </ErrorIcon>
                    <ErrorTitle>Badge Not Found</ErrorTitle>
                    <ErrorMessage>
                        This badge could not be verified or doesn't exist. The verification ID may be invalid.
                    </ErrorMessage>
                    <SecondaryButton onClick={goToAchievements}>
                        <Award size={20} />
                        View Achievements
                    </SecondaryButton>
                </ErrorContainer>
            </PageWrapper>
        );
    }

    // Extract data from the badge
    const {
        achievement,
        user,
        verification_id,
        earned_at,
        // Use additional fields if available in the API response
        achievement_name = achievement && achievement.includes(' - ') ? achievement.split(' - ')[0] : achievement,
        achievement_type = achievement && achievement.includes(' - ') ? achievement.split(' - ')[1] : 'achievement',
        issuer_name = badgeData.issuer_name || 'Organization',
        issuer_verified = badgeData.issuer_verified !== false,
        recipient_name = badgeData.recipient_name || `User #${user}`,
        description = badgeData.description || 'This credential certifies the successful completion of the achievement.',
        xp_reward = badgeData.xp_reward || badgeData.xp || 100,
    } = badgeData;

    // Get an icon based on achievement type
    const getAchievementIcon = (type) => {
        // If the backend provides an icon, use that instead
        if (badgeData.icon) return badgeData.icon;

        // Default icons based on type
        const typeMap = {
            'course_completion': '🎓',
            'module_completion': '📚',
            'challenge_completion': '🏆',
            'skill_mastery': '⭐',
            'ctf_win': '🚩',
            'bug_bounty': '🐛',
        };

        return typeMap[type] || '🔐';
    };

    const achievementIcon = getAchievementIcon(achievement_type);

    return (
        <PageWrapper>
            <PageContainer>
                <PageHeader>
                    <HeaderIcon>
                        <Shield />
                    </HeaderIcon>
                    <PageTitle>{badgeData.page_title || 'Badge Verification'}</PageTitle>
                    <PageSubtitle>
                        {badgeData.page_subtitle || 'The digital credential below has been cryptographically verified as authentic'}
                    </PageSubtitle>
                    <VerifiedBadge>
                        <Check size={16} /> {badgeData.verification_status || 'Verified Credential'}
                    </VerifiedBadge>
                </PageHeader>

                <BadgeCard ref={badgeRef}>
                    <BadgeBackground />
                    <BadgeScanlines />
                    <BadgeCardInner>
                        <BadgeHeader>
                            <BadgeIconContainer>
                                <BadgeIconRing />
                                <BadgeIcon>{achievementIcon}</BadgeIcon>
                            </BadgeIconContainer>

                            <BadgeInfo>
                                <BadgeTitle>{achievement_name}</BadgeTitle>
                                <BadgeDescription>
                                    {description}
                                </BadgeDescription>

                                <BadgeTags>
                                    <BadgeTag primary>
                                        <Check size={14} /> {badgeData.status || 'VERIFIED'}
                                    </BadgeTag>
                                    <BadgeTag>
                                        <Tag size={14} /> {achievement_type.toUpperCase()}
                                    </BadgeTag>
                                    {badgeData.tags && badgeData.tags.map((tag, index) => (
                                        <BadgeTag key={index}>
                                            {tag}
                                        </BadgeTag>
                                    ))}
                                </BadgeTags>
                            </BadgeInfo>
                        </BadgeHeader>

                        <BadgeMeta>
                            <MetaItem>
                                <MetaIcon>
                                    <Calendar size={18} />
                                </MetaIcon>
                                <MetaContent>
                                    <MetaLabel>Issued On</MetaLabel>
                                    <MetaValue>
                                        {new Date(earned_at).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </MetaValue>
                                </MetaContent>
                            </MetaItem>

                            <MetaItem>
                                <MetaIcon>
                                    <Hash size={18} />
                                </MetaIcon>
                                <MetaContent>
                                    <MetaLabel>Credential ID</MetaLabel>
                                    <MetaValue>{verification_id}</MetaValue>
                                </MetaContent>
                            </MetaItem>

                            <MetaItem>
                                <MetaIcon>
                                    <User size={18} />
                                </MetaIcon>
                                <MetaContent>
                                    <MetaLabel>Recipient</MetaLabel>
                                    <MetaValue>{recipient_name}</MetaValue>
                                </MetaContent>
                            </MetaItem>

                            {(xp_reward || badgeData.points) && (
                                <MetaItem>
                                    <MetaIcon>
                                        <Layers size={18} />
                                    </MetaIcon>
                                    <MetaContent>
                                        <MetaLabel>XP Reward</MetaLabel>
                                        <MetaValue>+{xp_reward} {badgeData.points_label || 'XP'}</MetaValue>
                                    </MetaContent>
                                </MetaItem>
                            )}

                            {/* Render any additional metadata from the backend */}
                            {badgeData.metadata && Object.entries(badgeData.metadata).map(([key, value], index) => (
                                <MetaItem key={index}>
                                    <MetaIcon>
                                        <Tag size={18} />
                                    </MetaIcon>
                                    <MetaContent>
                                        <MetaLabel>{key}</MetaLabel>
                                        <MetaValue>{value}</MetaValue>
                                    </MetaContent>
                                </MetaItem>
                            ))}
                        </BadgeMeta>

                        <IssuerSection>
                            <IssuerInfo>
                                <IssuerLogo>
                                    {badgeData.issuer_logo ? (
                                        <img src={badgeData.issuer_logo} alt="Issuer Logo" />
                                    ) : (
                                        <Terminal />
                                    )}
                                </IssuerLogo>
                                <IssuerDetails>
                                    <IssuerName>{issuer_name}</IssuerName>
                                    {issuer_verified && (
                                        <IssuerVerified>
                                            <Check size={12} /> {badgeData.issuer_verified_text || 'Verified Issuer'}
                                        </IssuerVerified>
                                    )}
                                </IssuerDetails>
                            </IssuerInfo>

                            <QRCode>
                                <img
                                    src={badgeData.qr_code || `https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(window.location.href)}`}
                                    alt="Verification QR Code"
                                />
                            </QRCode>
                        </IssuerSection>
                    </BadgeCardInner>
                </BadgeCard>

                <ActionButtons>
                    <PrimaryButton onClick={downloadBadgeImage} disabled={downloading}>
                        <Download size={20} />
                        {downloading ? 'Processing...' : (badgeData.download_label || 'Download Badge')}
                    </PrimaryButton>

                    <SecondaryButton onClick={visitPlatform}>
                        <ExternalLink size={20} />
                        {badgeData.platform_button_label || 'Visit Platform'}
                    </SecondaryButton>

                    {/* Render additional buttons if provided by backend */}
                    {badgeData.additional_buttons && badgeData.additional_buttons.map((button, index) => (
                        <SecondaryButton
                            key={index}
                            onClick={() => window.open(button.url, '_blank')}
                        >
                            {button.label}
                        </SecondaryButton>
                    ))}
                </ActionButtons>
            </PageContainer>

            {showToast && (
                <ToastNotification>
                    <ToastIcon>
                        <Terminal size={18} />
                    </ToastIcon>
                    <ToastMessage>{toastMessage}</ToastMessage>
                </ToastNotification>
            )}
        </PageWrapper>
    );
};

export default BadgeVerificationPage;